<template>
  <!-- Modal backdrop -->
  <div 
    v-if="isVisible" 
    class="modal-backdrop fade"
    :class="{ show: isVisible }"
    @click="closeModal"
  ></div>
  
  <!-- Bootstrap modal -->
  <div
    class="modal fade"
    :class="{ show: isVisible }"
    :style="{ display: isVisible ? 'block' : 'none' }"
    id="route-type-dialog"
    tabindex="-1"
    aria-labelledby="routeTypeDialogLabel"
    :aria-hidden="!isVisible"
    ref="dialog"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 route-type-dialog">
        <!-- HEADER ---------------------------------------------------->
        <div class="modal-header border-0">
          <h5 class="modal-title" id="routeTypeDialogLabel">Set route type</h5>
          <button
            type="button"
            class="btn-close"
            @click="closeModal"
            aria-label="Close"
          />
        </div>

        <!-- BODY ------------------------------------------------------>
        <div class="modal-body px-0">
          <ul class="list-unstyled m-0">
            <li v-for="(option, idx) in options" :key="option.key">
              <button
                class="route-option px-4 py-3 w-100 d-flex align-items-center justify-content-between"
                :class="{ active: selected === option.key }"
                @click="select(option.key)"
              >
                <!-- coloured dot / dashed preview + label -->
                <div class="d-flex align-items-center">
                  <span
                    :class="['route-symbol', option.style === 'dashed' ? 'route-symbol--dashed' : '']"
                    :style="{ backgroundColor: option.color }"
                    aria-hidden="true"
                  />
                  <span class="option-label ms-3" :style="{ color: option.color }">{{ option.label }}</span>
                </div>

                <!-- checkmark -->
                <svg
                  v-if="selected === option.key"
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  class="bi bi-check-lg"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M13.485 1.72a.75.75 0 0 1 1.06 1.06L6.78 10.546 2.454 6.222a.75.75 0 0 1 1.06-1.06l3.097 3.096 6.874-6.878z"
                  />
                </svg>
              </button>

              <!-- divider -->
              <hr v-if="idx !== options.length - 1" class="m-0 route-divider" />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onUnmounted } from 'vue';
import emitter from '../../../utils/bus';
import { getRouteStyleSettings } from '../../../utils/routeStyleSettings';

function buildOptions() {
  const s = getRouteStyleSettings();
  return [
    { key: 'route',     label: 'Route',     color: s.main.color,  style: 'solid'  },
    { key: 'alt',       label: 'Alt',       color: s.alt.color,   style: 'solid'  },
    { key: 'spur',      label: 'Spur',      color: s.spur.color,  style: 'solid'  },
    { key: 'transport', label: 'Transport', color: s.main.color,  style: 'dashed' },
  ];
}

export default defineComponent({
  name: 'RouteStyleDialog',
  props: {
    selected: {
      type: String as () => 'route' | 'alt' | 'spur' | 'transport',
      default: 'route'
    }
  },
  emits: ['update:selected', 'close'],
  setup(props, { emit, expose }) {
    const options = ref(buildOptions());

    type RouteKey = 'route' | 'alt' | 'spur' | 'transport';
    const localSelected = ref<RouteKey>(props.selected);
    const isVisible = ref(false);

    const onSettingsChanged = () => { options.value = buildOptions(); };
    emitter.on('route-style-settings:changed', onSettingsChanged);
    onUnmounted(() => emitter.off('route-style-settings:changed', onSettingsChanged));

    // Watch for prop changes
    watch(() => props.selected, (newValue) => {
      localSelected.value = newValue;
    });

    function select(key: RouteKey) {
      localSelected.value = key;
      emit('update:selected', key);
      closeModal();
    }
    
    function closeModal() {
      isVisible.value = false;
      // Remove modal-open class from body
      document.body.classList.remove('modal-open');
      emit('close');
    }
    
    function show() {
      isVisible.value = true;
      // Add modal-open class to body
      document.body.classList.add('modal-open');
    }

    // Handle ESC key
    function handleKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isVisible.value) {
        closeModal();
      }
    }

    // Add/remove keydown listener based on visibility
    watch(isVisible, (newVal) => {
      if (newVal) {
        document.addEventListener('keydown', handleKeydown);
      } else {
        document.removeEventListener('keydown', handleKeydown);
      }
    });

    // Expose the show method
    expose({ show });
    
    return { options, selected: localSelected, select, closeModal, isVisible };
  },
});
</script>

<style scoped>
/* ---------- modal shell ---------- */
.route-type-dialog {
  background-color: #1c1d22;
  color: #ffffff;
  border-radius: 8px;
  min-width: 330px;
}

.route-type-dialog .modal-header {
  background-color: transparent;
}

/* ---------- option rows ---------- */
.route-option {
  background: transparent;
  border: none;
  color: inherit;
  text-align: left;
  font-size: 1rem;
  transition: background-color 0.15s ease-in-out;
}

.route-option:hover {
  background-color: #24252b;
}

.route-option:hover .option-label {
  text-shadow: 0 0 8px currentColor;
}

.route-option.active {
  background-color: #2a2b31;
}

.route-option.active .option-label {
  font-weight: 700;
  text-shadow: 0 0 12px currentColor;
}

/* ---------- coloured dots / dashed line ---------- */
.route-symbol {
  display: inline-block;
  width: 24px;
  height: 4px;
  border-radius: 2px;
}

.route-symbol--dashed {
  /* dashed horizontal preview */
  width: 24px;
  height: 4px;
  border-radius: 0;
  background-image: repeating-linear-gradient(
    90deg,
    var(--dark),
    var(--dark) 4px,
    transparent 4px,
    transparent 8px
  ) !important;
}

.option-label {
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
}

/* ---------- divider line ---------- */
.route-divider {
  border-color: #2e2f36;
}
</style>
